import { useState, useEffect, useRef } from 'react';
import { supa } from '../lib/supabase.js';
import { TH, MP, REWARDS_CATALOG, INIT_FILTERS, IMG_FILTER_SAMPLE } from '../lib/constants.js';
import { isPhotoUrl, isThought, thoughtFontStyle, drawShareImage, pushToast, streakDisplayCount, sectionGlowBg } from '../lib/helpers.js';
import { YR, DOY, daysInYear } from '../lib/dateState.js';
import { fetchRealFeed, fetchUnreadNotifCount, fetchMyThreads, fetchMyFollowing, sharePageToRealThread } from '../lib/api.js';
import { t as tr } from '../lib/i18n.js';
import { Av, Btn, Logo, FlameBadge } from '../components/Basic.jsx';
import { NavIcon } from '../components/Navigation.jsx';
import { FilterTile } from '../components/FilterTile.jsx';
// Icona "libro chiuso con segnalibro" per la riga in alto al feed — prima erano
// quadrati con una sottile striscia scura a sinistra, troppo simili alle
// storie di Instagram (cerchio a parte). Ora è più rettangolare (più alta
// che larga, come una copertina), con un nastro/segnalibro che sporge da
// sotto — stessa idea del logo, per far capire a colpo d'occhio che è un
// libro, non una storia.
function StoryBook({t,bg,emoji,badge,ribbon}){
  // Rifatto in 3D il 01/09/2026 (Matte: "è ancora un disegno" — restava
  // piatta anche dopo il resto dell'upgrade "in rilievo"). Stessa idea del
  // libro sullo scaffale in Shelf.jsx e della copertina del Notebook: non
  // basta un blocco di colore col bordino, serve luce vera. Aggiunti — un
  // taglio di pagine sul lato destro (righe chiare ripetute, come un vero
  // libro chiuso visto di taglio), un'ombra di dorso sul lato sinistro
  // della copertina, un riflesso lucido in alto, e un'ombra a più strati
  // (proiettata + riflessi interni) invece della singola ombra piatta di
  // prima. Il segnalibro ha ora un piccolo gradiente e un'ombra propria
  // invece di un colore piatto.
  return(
    <div style={{position:"relative",width:44,height:62,flexShrink:0}}>
      {/* Il segnalibro sta DIETRO la copertina: parte da dentro il libro e ne
          esce solo la punta da sotto, come infilato davvero tra le pagine —
          non appoggiato sopra. */}
      <div style={{position:"absolute",left:"50%",top:36,transform:"translateX(-50%)",width:12,height:26,background:`linear-gradient(100deg,${ribbon||"#FBF3E7"},${ribbon||"#FBF3E7"}CC)`,clipPath:"polygon(0 0,100% 0,100% 50%,50% 100%,0 50%)",zIndex:1,boxShadow:"0 2px 3px rgba(0,0,0,.22)"}}/>
      {/* Taglio delle pagine — sottili righe chiare/scure alternate, come la
          costa di un libro vero visto di lato. */}
      <div style={{position:"absolute",top:1,left:40,width:4,height:48,borderRadius:"0 2px 2px 0",background:"repeating-linear-gradient(180deg,#fff,#fff 1.5px,#e4dcc8 1.5px,#e4dcc8 3px)",boxShadow:"inset -1px 0 2px rgba(0,0,0,.18)",zIndex:1}}/>
      <div style={{position:"absolute",top:0,left:0,width:44,height:50,borderRadius:"4px 9px 9px 4px",background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:21,boxShadow:`0 0 0 2px ${t.bg},0 6px 12px rgba(0,0,0,.32),0 2px 4px rgba(0,0,0,.22),inset 0 1.5px 0 rgba(255,255,255,.3),inset 0 -2px 4px rgba(0,0,0,.22)`,overflow:"hidden",zIndex:2}}>
        {/* Ombra del dorso: la copertina non è più un piano unico, si vede
            dove "piega" verso la costa. */}
        <div style={{position:"absolute",left:0,top:0,bottom:0,width:6,background:"linear-gradient(90deg,rgba(0,0,0,.4),transparent)",zIndex:1,pointerEvents:"none"}}/>
        {/* Riflesso lucido in alto, come una copertina plastificata vera. */}
        <div style={{position:"absolute",top:0,left:0,right:0,height:"48%",background:"linear-gradient(180deg,rgba(255,255,255,.3),transparent)",zIndex:1,pointerEvents:"none"}}/>
        <span style={{position:"relative",zIndex:2}}>{emoji}</span>
      </div>
      {badge}
    </div>
  );
}
export function FollowedBookViewer({pages,onClose}){
  const [focusedIndex,setFocusedIndex]=useState(null); // null = grid overview
  const [flip,setFlip]=useState(null);
  const flipTimer=useRef(null);
  const touchRef=useRef({x:0,active:false});
  const goToPage=(newIndex)=>{
    if(newIndex<0||newIndex>=pages.length||newIndex===focusedIndex||flip) return;
    const dir=newIndex>focusedIndex?"next":"prev";
    setFlip({from:focusedIndex,to:newIndex,dir});
    clearTimeout(flipTimer.current);
    flipTimer.current=setTimeout(()=>{ setFocusedIndex(newIndex); setFlip(null); },300);
  };
  const renderPage=(idx)=>{
    const p=pages[idx];
    if(!p) return null;
    const preset=MP.find(x=>x.id===p.bookPreset)||MP[0];
    return(
      <div style={{position:"absolute",inset:0,background:"#111",display:"flex",flexDirection:"column"}}>
        <div style={{flex:1,position:"relative",background:`linear-gradient(135deg,${preset.ac}33,#000)`,overflow:"hidden"}}>
          {isPhotoUrl(p.photo)
            ?<img src={p.photo} loading="eager" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
            :isThought(p.photo)
              ?<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",padding:22,overflow:"hidden"}}><span style={{...thoughtFontStyle(p.photo),fontSize:17,lineHeight:1.5,color:"#fff",textAlign:"center"}}>{p.caption||"✏️"}</span></div>
              :<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:60}}>{p.photo}</div>}
        </div>
        <div style={{padding:"14px 16px 22px",background:"#111",flexShrink:0}}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif"}}>@{p.username} · {p.bookTitle}</div>
          {p.caption&&<div style={{fontSize:12,color:"rgba(255,255,255,.7)",fontFamily:"Inter,sans-serif",marginTop:4}}>{p.caption}</div>}
        </div>
      </div>
    );
  };
  return(
    <div style={{position:"fixed",inset:0,zIndex:300,background:"#000"}}>
      <div onClick={focusedIndex===null?onClose:()=>setFocusedIndex(null)} style={{position:"absolute",top:15,left:15,zIndex:30,width:32,height:32,borderRadius:"50%",background:"rgba(255,255,255,.15)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#fff",fontSize:14}}>←</div>
      {focusedIndex===null?(
        <div style={{padding:"60px 16px 30px",height:"100%",overflowY:"auto",boxSizing:"border-box"}}>
          <div style={{fontSize:18,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",marginBottom:4,textAlign:"center"}}>{tr("feed_from_followed_books")}</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,.5)",fontFamily:"Inter,sans-serif",marginBottom:20,textAlign:"center"}}>{pages.length} {pages.length===1?tr("feed_page_singular"):tr("feed_page_plural")}</div>
          {pages.length===0?(
            <div style={{textAlign:"center",padding:"50px 24px",color:"rgba(255,255,255,.55)",fontFamily:"Inter,sans-serif"}}>
              <div style={{fontSize:38,marginBottom:14}}>📖</div>
              <div style={{fontSize:13,lineHeight:1.6}}>{tr("feed_followed_empty_1")}<br/>{tr("feed_followed_empty_2")}</div>
            </div>
          ):(
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
            {pages.map((p,i)=>(
              <div key={p.id} onClick={()=>setFocusedIndex(i)} style={{aspectRatio:"1",borderRadius:10,overflow:"hidden",position:"relative",cursor:"pointer",background:"#222"}}>
                {isPhotoUrl(p.photo)
                  ?<img src={p.photo} loading="lazy" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
                  :isThought(p.photo)
                    ?<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",padding:6,overflow:"hidden"}}><span style={{...thoughtFontStyle(p.photo),fontSize:8,lineHeight:1.25,color:"#fff",textAlign:"center",display:"-webkit-box",WebkitLineClamp:4,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{p.caption||"✏️"}</span></div>
                    :<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>{p.photo}</div>}
                <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"4px 6px",background:"linear-gradient(0deg,rgba(0,0,0,.75),transparent)",fontSize:8,color:"#fff",fontFamily:"Inter,sans-serif",fontWeight:700}}>@{p.username}</div>
              </div>
            ))}
          </div>
          )}
        </div>
      ):(
        <div style={{position:"relative",height:"100%"}}
          onTouchStart={e=>{ touchRef.current={x:e.touches[0].clientX,active:true}; }}
          onTouchEnd={e=>{
            if(!touchRef.current.active) return;
            const dx=e.changedTouches[0].clientX-touchRef.current.x;
            touchRef.current.active=false;
            if(Math.abs(dx)<50) return;
            if(dx<0) goToPage(focusedIndex+1); else goToPage(focusedIndex-1);
          }}>
          <div className="mb-flip-layer mb-flip-to">{renderPage(flip?flip.to:focusedIndex)}</div>
          {flip&&<div className={`mb-flip-layer mb-flip-from dir-${flip.dir}`}>{renderPage(flip.from)}</div>}
          {focusedIndex>0&&<div onClick={()=>goToPage(focusedIndex-1)} style={{position:"absolute",left:0,top:0,bottom:90,width:"28%",zIndex:20}}/>}
          {focusedIndex<pages.length-1&&<div onClick={()=>goToPage(focusedIndex+1)} style={{position:"absolute",right:0,top:0,bottom:90,width:"28%",zIndex:20}}/>}
        </div>
      )}
    </div>
  );
}
export function ShareSheet({item,filter,ut,onClose,dispatch}){
  const canvasRef=useRef(null);
  const drawPromiseRef=useRef(null);
  const [busy,setBusy]=useState(false);
  // La foto va ricaricata da rete con un flag tecnico (crossOrigin) diverso
  // da quello con cui è già mostrata altrove nell'app — anche se il
  // browser la ha già in cache, questo caricamento riparte da capo la
  // prima volta. Senza un indicatore, il riquadro restava vuoto e sembrava
  // bloccato invece che "in corso" — da qui questo stato.
  const [previewLoading,setPreviewLoading]=useState(true);
  const canNativeShare=typeof navigator!=="undefined"&&!!navigator.share;
  useEffect(()=>{
    if(canvasRef.current){
      setPreviewLoading(true);
      drawPromiseRef.current=drawShareImage(canvasRef.current,item,filter,ut);
      drawPromiseRef.current.finally(()=>setPreviewLoading(false));
    }
  },[]);
  const getBlob=async()=>{
    if(drawPromiseRef.current){ try{ await drawPromiseRef.current; }catch(e){} }
    return new Promise(res=>canvasRef.current.toBlob(b=>res(b),"image/jpeg",0.92));
  };
  const handleNative=async()=>{
    setBusy(true);
    try{
      const blob=await getBlob();
      const file=new File([blob],`oncer-pag${item.day}.jpg`,{type:"image/jpeg"});
      if(navigator.canShare&&navigator.canShare({files:[file]})){
        await navigator.share({files:[file],title:"Oncer",text:tr("feed_native_share_text")});
      }else if(navigator.share){
        await navigator.share({title:"Oncer",text:tr("feed_share_message")});
      }
    }catch(e){
      if(e&&e.name!=="AbortError"){ console.error("Oncer: condivisione nativa fallita —",e.message); pushToast(dispatch,tr("feed_toast_share_failed")); }
    }
    setBusy(false);
  };
  const handleDownload=async()=>{
    setBusy(true);
    const blob=await getBlob();
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url; a.download=`oncer-pagina-${item.day}.jpg`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setBusy(false);
  };
  const shareText=encodeURIComponent(tr("feed_share_message"));
  const platforms=[
    {id:"whatsapp",label:"WhatsApp",bg:"#25D366",url:`https://wa.me/?text=${shareText}`},
    {id:"x",       label:"X",       bg:"#111",    url:`https://twitter.com/intent/tweet?text=${shareText}`},
    {id:"facebook",label:"Facebook",bg:"#1877F2", url:`https://www.facebook.com/sharer/sharer.php?u=https://oncer.app&quote=${shareText}`},
  ];
  return(
    <div className="overlay" onClick={onClose}>
      <div className="sheet" onClick={e=>e.stopPropagation()} style={{maxHeight:"92vh"}}>
        <div style={{display:"flex",justifyContent:"center",padding:"11px 0 4px"}}><div style={{width:36,height:4,borderRadius:2,background:"rgba(0,0,0,.12)"}}/></div>
        <div style={{padding:"5px 16px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:15,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif"}}>{tr("feed_share_external_title")}</span>
          <span onClick={onClose} style={{cursor:"pointer",fontSize:20,color:"#aaa"}}>✕</span>
        </div>
        <style>{`@keyframes onceShareSpin{to{transform:rotate(360deg);}}`}</style>
        <div style={{padding:"0 16px 12px",display:"flex",justifyContent:"center"}}>
          <div style={{width:216,height:270,borderRadius:16,overflow:"hidden",boxShadow:"0 6px 20px rgba(0,0,0,.18)",position:"relative",background:"#f2f2f2"}}>
            <canvas ref={canvasRef} style={{width:"100%",height:"100%",display:"block"}}/>
            {previewLoading&&(
              <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,background:"#f2f2f2"}}>
                <div style={{width:28,height:28,borderRadius:"50%",border:"3px solid rgba(0,0,0,.12)",borderTopColor:ut.ac,animation:"onceShareSpin .8s linear infinite"}}/>
                <span style={{fontSize:11,color:"#999",fontFamily:"Inter,sans-serif"}}>{tr("feed_share_preparing")}</span>
              </div>
            )}
          </div>
        </div>
        <div style={{padding:"0 16px 14px",textAlign:"center",fontSize:10.5,color:"#999",fontFamily:"Inter,sans-serif",lineHeight:1.5}}>
          {tr("feed_share_watermark_note")}
        </div>
        {canNativeShare?(
          <div style={{padding:"0 16px 12px"}}>
            <Btn full fn={handleNative} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"14px",borderRadius:40,fontSize:14,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",opacity:busy?.6:1}}>
              {busy?tr("feed_share_preparing"):tr("feed_share_photo_btn")}
            </Btn>
          </div>
        ):(
          <div style={{padding:"0 16px 10px",fontSize:10.5,color:"#999",fontFamily:"Inter,sans-serif",textAlign:"center",lineHeight:1.5}}>{tr("feed_share_no_native_note")}</div>
        )}
        <div style={{padding:"0 16px 8px",fontSize:10,fontWeight:700,color:"#999",fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase"}}>{tr("feed_share_or_choose")}</div>
        <div style={{padding:"0 16px 10px",display:"flex",gap:9}}>
          {platforms.map(p=>(
            <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{flex:1,textDecoration:"none"}}>
              <div style={{background:p.bg,color:"#fff",padding:"12px 0",borderRadius:13,textAlign:"center",fontSize:12,fontWeight:700,fontFamily:"Inter,sans-serif"}}>{p.label}</div>
            </a>
          ))}
        </div>
        <div style={{padding:"4px 16px 10px"}}>
          <div onClick={handleDownload} style={{background:"#f2f2f2",color:"#333",padding:"13px",borderRadius:13,textAlign:"center",fontSize:12.5,fontWeight:700,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>{tr("feed_share_save_image")}</div>
        </div>
        <div style={{padding:"0 16px 24px",fontSize:10,color:"#aaa",fontFamily:"Inter,sans-serif",textAlign:"center",lineHeight:1.5}}>
          {tr("feed_share_ig_tiktok_note")}
        </div>
      </div>
    </div>
  );
}
// Set di reazioni rapide mostrate nel picker (31/08/2026, richiesta di
// Matte). Sono scritte come normali caratteri emoji Unicode — come già
// fatto ovunque in questo file — quindi ogni telefono le disegna con il
// proprio font di sistema: stile Apple su iPhone, stile Google/Noto su
// Android, senza bisogno di includere immagini di terzi nell'app.
const DEFAULT_REACTIONS=["❤️","😊","🥹","👏","😂","😮","✨","😌"];
export function ReactionBar({item,t,dispatch,userId}){
  const [myReactedEmoji,setMyReactedEmoji]=useState(item.myReaction||null); // una sola reazione attiva per persona, mai più di una — letta dal database, non "a memoria"
  const [showPicker,setShowPicker]=useState(false);
  const [showInput,setShowInput]=useState(false);
  const [draft,setDraft]=useState("");
  const reactions=item.reactions||{};
  const activeEmojis=Object.keys(reactions).filter(e=>reactions[e]>0).sort((a,b)=>reactions[b]-reactions[a]);
  useEffect(()=>{ setMyReactedEmoji(item.myReaction||null); },[item.myReaction]);
  const react=async(emoji)=>{
    const clean=(emoji||"").trim().slice(0,8);
    if(!clean)return;
    setShowPicker(false);
    setShowInput(false);
    if(clean===myReactedEmoji){
      // Toccare di nuovo la propria reazione attuale la toglie.
      dispatch({type:"REACT",itemId:item.id,emoji:null,previousEmoji:clean});
      setMyReactedEmoji(null);
      if(item.real&&item.pageId&&userId){
        const {error}=await supa.from("reactions").delete().eq("page_id",item.pageId).eq("user_id",userId).eq("emoji",clean);
        if(error){ console.error("Oncer: rimozione reazione fallita —",error.message); pushToast(dispatch,tr("feed_toast_reaction_remove_failed")); }
      }
      return;
    }
    // Una sola reazione a testa: sceglierne una nuova sostituisce quella
    // precedente, non si aggiunge accanto.
    const previous=myReactedEmoji;
    dispatch({type:"REACT",itemId:item.id,emoji:clean,previousEmoji:previous});
    setMyReactedEmoji(clean);
    if(item.real&&item.pageId&&userId){
      if(previous){
        const {error:delErr}=await supa.from("reactions").delete().eq("page_id",item.pageId).eq("user_id",userId).eq("emoji",previous);
        if(delErr) console.error("Oncer: rimozione vecchia reazione fallita —",delErr.message);
      }
      const {error}=await supa.from("reactions").insert({page_id:item.pageId,user_id:userId,emoji:clean});
      if(error){ console.error("Oncer: reazione non salvata sul database —",error.message); pushToast(dispatch,tr("feed_toast_reaction_save_failed")); }
      else if(item.ownerId&&item.ownerId!==userId){
        // Mai una notifica per una reazione alla propria stessa foto.
        const {error:notifErr}=await supa.from("notifications").insert({user_id:item.ownerId,type:"reaction",from_user_id:userId,data:{emoji:clean,page_id:item.pageId}});
        if(notifErr) console.error("Oncer: notifica reazione non creata —",notifErr.message);
      }
    }
  };
  return(
    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",flex:1}}>
      {/* Emoji già usate mostrate "nude" — 31/08/2026 (Matte): niente più
          sfondo/bordo/numero accanto, sembravano badge finti tipo "mi
          piace" con contatore. Restano solo per far vedere QUALI emoji
          sono state scelte, non quante volte. La mia reazione (se c'è)
          resta a piena opacità, le altre più sbiadite. */}
      {activeEmojis.map(e=>(
        <span key={e} onClick={()=>react(e)} style={{fontSize:17,cursor:"pointer",opacity:myReactedEmoji===e?1:.5,lineHeight:1,flexShrink:0}}>{e}</span>
      ))}
      {/* Pulsante reagisci — un tondo pieno color accento con solo un "+",
          niente etichetta testuale: deve leggersi da solo come pulsante
          (stesso linguaggio del pulsante "Invia"), non come un'altra
          emoji decorativa. Un tocco apre il piccolo pannello sotto con le
          reazioni rapide + il "+" per una personalizzata. */}
      {!showPicker?(
        <span onClick={()=>setShowPicker(true)} style={{width:28,height:28,borderRadius:"50%",background:t.ac,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,color:"#fff",cursor:"pointer",flexShrink:0,lineHeight:1}}>+</span>
      ):(
        <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap",background:t.bg+"40",backdropFilter:"blur(10px) saturate(180%)",WebkitBackdropFilter:"blur(10px) saturate(180%)",border:`1px solid ${t.tx}22`,borderRadius:20,padding:"5px 8px"}}>
          {DEFAULT_REACTIONS.map(e=>(
            <span key={e} onClick={()=>react(e)} style={{fontSize:17,cursor:"pointer",lineHeight:1}}>{e}</span>
          ))}
          {!showInput?(
            <span onClick={()=>setShowInput(true)} style={{width:19,height:19,borderRadius:"50%",background:t.tx+"1a",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:t.soft,fontWeight:700,cursor:"pointer",flexShrink:0}}>+</span>
          ):(
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
              <input
                autoFocus
                value={draft}
                onChange={e=>setDraft(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){ react(draft); setDraft(""); } if(e.key==="Escape"){ setShowInput(false); setDraft(""); } }}
                onBlur={()=>{ if(!draft) setShowInput(false); }}
                placeholder="🙂"
                style={{width:46,border:`1px solid ${t.bord}`,borderRadius:20,padding:"3px 8px",fontSize:15,fontFamily:"Inter,sans-serif",background:t.bg,color:t.tx}}
              />
              <span onClick={()=>{react(draft);setDraft("");}} style={{fontSize:10,color:t.ac,fontWeight:700,cursor:"pointer",fontFamily:"Inter,sans-serif"}}>OK</span>
            </span>
          )}
          <span onClick={()=>{setShowPicker(false);setShowInput(false);}} style={{width:19,height:19,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:t.soft,cursor:"pointer",flexShrink:0}}>✕</span>
        </div>
      )}
    </div>
  );
}
export function FeedCard({item,t,go,dispatch,threads,realRecipients,filters,userId,eager}){
  const [sendingTo,setSendingTo]=useState(null);
  const ut=TH[item.ut]||TH.neon;
  const [showShare,setShowShare]=useState(false);
  const [showExternal,setShowExternal]=useState(false);
  const itemFilter=(filters||INIT_FILTERS).find(f=>f.id===item.filter)||null;
  return(
    <div style={{background:t.card,borderRadius:18,overflow:"hidden",border:`1px solid ${t.bord}`,boxShadow:"0 2px 14px rgba(0,0,0,.11)",marginBottom:14}}>
      <div style={{padding:"12px 13px 8px",display:"flex",alignItems:"center",gap:9}}>
        <div onClick={()=>go("profile_"+item.user)} style={{cursor:"pointer"}}><Av u={item.user} tid={ut.id} sz={38} photo={item.avatarUrl}/></div>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span onClick={()=>go("profile_"+item.user)} style={{fontSize:13,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>@{item.user}</span>
            {item.verified&&(
              <svg width="12" height="16" viewBox="0 0 26 36" fill="none" style={{flexShrink:0}}>
                <defs>
                  <linearGradient id={`oncerVerGrad${item.id}`} x1="2" y1="2" x2="24" y2="34" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#FF2D6B"/>
                    <stop offset="1" stopColor="#7B2FFF"/>
                  </linearGradient>
                </defs>
                <path d="M5 2 L21 2 A3 3 0 0 1 24 5 L24 34 L13 26 L2 34 L2 5 A3 3 0 0 1 5 2 Z" fill={`url(#oncerVerGrad${item.id})`}/>
                <path d="M8 16 L11.5 19.5 L18 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            )}
            {(()=>{
              const on=streakDisplayCount(item.dailyStreak,DOY,YR);
              return <FlameBadge count={on} sz={20} title={on?tr("prof_streak_badge_title",{n:on}):tr("prof_streak_badge_off_title")}/>;
            })()}
            {item.micro&&<span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:20,background:item.micro.ac+"22",fontSize:9,fontFamily:"Inter,sans-serif",fontWeight:700,color:item.micro.ac}}>{item.micro.icon} {item.micro.title}</span>}
          </div>
          {/* Meta unificata (31/08/2026, confermata a Matte prima di
              implementare): via l'emoji 🎞 e il sottolineato che sembravano
              un link — filtro, data e giorno ora stanno su un'unica riga
              discreta, niente più badge tema ridondante sopra. */}
          <div onClick={()=>itemFilter&&itemFilter.id!=="none"&&go("filters",{highlightId:itemFilter.id})} style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",marginTop:2,cursor:itemFilter&&itemFilter.id!=="none"?"pointer":"default"}}>
            {item.micro
              ?`${item.micro.icon} ${item.micro.title} · ${tr("feed_page_abbr")} ${item.day}`
              :(itemFilter&&itemFilter.id!=="none"?`${itemFilter.name} · `:"")+`${item.date} · ${tr("feed_page_abbr")} ${item.day}`}
          </div>
        </div>
      </div>
      <div style={{width:"100%",aspectRatio:isThought(item.photo)?"auto":"1",minHeight:isThought(item.photo)?140:undefined,background:`linear-gradient(135deg,${ut.ac}28,${ut.ac2}18)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:86,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,background:ut.pat,pointerEvents:"none",zIndex:1}}/>
        {isThought(item.photo)
          ? <div style={{position:"relative",zIndex:1,padding:"26px 22px",textAlign:"center"}}>
              <div style={{fontSize:22,marginBottom:10,opacity:.7}}>✏️</div>
              <div style={{fontSize:15,color:t.tx,...thoughtFontStyle(item.photo),lineHeight:1.6,maxWidth:260}}>{item.cap}</div>
            </div>
          : isPhotoUrl(item.photo)
            ? <img src={item.photo} loading={eager?"eager":"lazy"} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",filter:itemFilter&&itemFilter.css&&itemFilter.css!=="none"?itemFilter.css:"none",zIndex:0}}/>
            : <span style={{position:"relative",zIndex:1,filter:itemFilter&&itemFilter.css&&itemFilter.css!=="none"?itemFilter.css:"none"}}>{item.photo}</span>}
        {itemFilter&&itemFilter.tint&&!isThought(item.photo)&&<div style={{position:"absolute",inset:0,background:itemFilter.tint,mixBlendMode:"overlay",zIndex:2,pointerEvents:"none"}}/>}
        {itemFilter&&itemFilter.vig&&!isThought(item.photo)&&<div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,.42) 100%)",zIndex:3,pointerEvents:"none"}}/>}
        <div style={{position:"absolute",bottom:9,right:11,fontSize:9,fontFamily:"Inter,sans-serif",color:isThought(item.photo)?t.soft:"#fff",textShadow:isThought(item.photo)?"none":"0 1px 3px rgba(0,0,0,.6)",letterSpacing:"1px",textTransform:"uppercase",zIndex:4}}>{item.micro?`Oncer · ${item.micro.title}`:`Oncer · ${YR}`}</div>
        {item.sticker&&!isThought(item.photo)&&<div style={{position:"absolute",top:10,left:10,fontSize:30,filter:"drop-shadow(0 2px 4px rgba(0,0,0,.4))",zIndex:4}}>{REWARDS_CATALOG.find(r=>r.id===item.sticker)?.emoji}</div>}
      </div>
      <div style={{padding:"10px 13px 13px"}}>
        {item.cap&&<div style={{fontSize:13,color:t.tx,fontFamily:"Inter,sans-serif",marginBottom:8,lineHeight:1.4}}><span style={{color:t.soft,fontWeight:600}}>@{item.user} </span>{item.cap}</div>}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <ReactionBar item={item} t={t} dispatch={dispatch} userId={userId}/>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            {/* Condividi/Invia alleggeriti (31/08/2026, confermato a Matte
                prima di implementare): non più due pillole piene identiche —
                condividi resta solo un'icona, invia icona+testo minimal. */}
            <span onClick={()=>setShowExternal(true)} style={{display:"flex",alignItems:"center",cursor:"pointer"}}>
              <NavIcon type="share" active={false} soft={t.soft} size={17}/>
            </span>
            {/* Fix 01/09/2026 (Matte: "ancora un'emoji brutta" sull'invia
                interno): non era un'emoji vera, ma la freccia "↗" testuale
                dentro la traduzione, raddoppiata sopra l'icona già presente
                — su Android quella freccia viene disegnata dal font emoji
                del sistema ed è quello che stonava. Tolta dalla traduzione
                (vedi i18n.js), e l'icona passa dal fumetto chat (generico,
                già usato per Notebook) a un aeroplanino di carta dedicato:
                si legge subito come "invia", non come "apri una chat". */}
            <span onClick={()=>setShowShare(true)} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:t.ac,fontWeight:600,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>
              <NavIcon type="send" active={false} soft={t.ac} size={15}/>{tr("feed_send_label")}
            </span>
          </div>
        </div>
      </div>
      {showExternal&&<ShareSheet item={item} filter={itemFilter} ut={ut} onClose={()=>setShowExternal(false)} dispatch={dispatch}/>}
      {showShare&&(
        <div className="overlay" onClick={()=>setShowShare(false)}>
          <div className="sheet" onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"center",padding:"11px 0 4px"}}><div style={{width:36,height:4,borderRadius:2,background:"rgba(0,0,0,.12)"}}/></div>
            <div style={{padding:"5px 16px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:15,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif"}}>{tr("feed_send_page_to")}</span>
              <span onClick={()=>setShowShare(false)} style={{cursor:"pointer",fontSize:20,color:"#aaa"}}>✕</span>
            </div>
            <div style={{padding:"0 15px 26px",display:"flex",flexDirection:"column",gap:8,maxHeight:"50vh",overflowY:"auto"}}>
              {(realRecipients||[]).map(r=>(
                <div key={"real-"+r.username} onClick={async()=>{
                  if(sendingTo)return;
                  setSendingTo(r.username);
                  const ok=await sharePageToRealThread(userId,r.id,{photo:item.photo,day:item.day,cap:item.cap,user:item.user});
                  setSendingTo(null);
                  if(ok){ setShowShare(false); go("notebook",{user:r.username}); }
                  else pushToast(dispatch,tr("feed_toast_send_failed"));
                }} style={{display:"flex",alignItems:"center",gap:11,padding:"10px 12px",borderRadius:12,border:"1px solid rgba(0,0,0,.08)",cursor:"pointer",opacity:sendingTo&&sendingTo!==r.username?.5:1}}>
                  <Av u={r.username} tid={r.theme||"neon"} sz={38} photo={r.avatarUrl}/>
                  <span style={{fontSize:14,fontWeight:700,color:"#1a1a1a",fontFamily:"Inter,sans-serif",flex:1}}>@{r.username}</span>
                  {sendingTo===r.username&&<span style={{fontSize:11,color:"#999",fontFamily:"Inter,sans-serif"}}>{tr("feed_sending")}</span>}
                </div>
              ))}
              {(threads||[]).map(th=>(
                <div key={th.id} onClick={()=>{
                  dispatch({type:"SHARE_PAGE",threadId:th.id,page:{photo:item.photo,day:item.day,cap:item.cap,user:item.user}});
                  setShowShare(false);
                  go("notebook",{user:th.user});
                }} style={{display:"flex",alignItems:"center",gap:11,padding:"10px 12px",borderRadius:12,border:"1px solid rgba(0,0,0,.08)",cursor:"pointer"}}>
                  <Av u={th.user} tid={"neon"} sz={38}/>
                  <span style={{fontSize:14,fontWeight:700,color:"#1a1a1a",fontFamily:"Inter,sans-serif"}}>@{th.user}</span>
                </div>
              ))}
              {(!threads||threads.length===0)&&(!realRecipients||realRecipients.length===0)&&<div style={{fontSize:12,color:"#999",fontFamily:"Inter,sans-serif",textAlign:"center",padding:"12px 0"}}>{tr("feed_follow_to_send")}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export function FeedView({t,theme,st,dispatch,go,userId}){
  // Segnalato da Matte il 31/08/2026: la barra doveva far vedere a che punto
  // dell'ANNO sei (calendario), non quante pagine hai postato — prima si
  // riempiva in base alle pagine davvero scattate (poteva restare quasi
  // vuota anche a novembre, se avevi saltato tanti giorni), mentre il
  // numero accanto ("pag 243") era già il giorno dell'anno corrente: due
  // informazioni diverse mescolate nello stesso controllo. Ora la barra
  // segue SOLO DOY/giorni-totali-dell'anno (366 negli anni bisestili),
  // coerente col numero che le sta a fianco.
  const yearProgress=(DOY/daysInYear(YR))*100;
  const todayDone=st.books[YR]?.pages?.some(p=>p.day===DOY);
  const unread=st.threads.reduce((a,b)=>a+b.unread,0);
  const seenIdsSet=new Set(st.seenMicroPageIds||[]);
  const unseenMicroCount=(st.followedMicroPages||[]).filter(p=>!seenIdsSet.has(p.id)).length;
  const [nowHM,setNowHM]=useState(()=>{ const d=new Date(); return d.getHours()*60+d.getMinutes(); });
  useEffect(()=>{ const iv=setInterval(()=>{ const d=new Date(); setNowHM(d.getHours()*60+d.getMinutes()); },60000); return ()=>clearInterval(iv); },[]);
  // Il numero sulla campanella arriva dal database solo all'apertura
  // dell'app — se una notifica arriva mentre sei già dentro, senza questo
  // controllo periodico il pallino non si aggiornerebbe da solo.
  useEffect(()=>{
    if(!userId) return;
    const check=()=>{ fetchUnreadNotifCount(userId).then(count=>dispatch({type:"SET_UNREAD_NOTIF_COUNT",count})); };
    check();
    const iv=setInterval(check,20000);
    return ()=>clearInterval(iv);
  },[userId]);
  const notifTime=(st.settings&&st.settings.notifTime)||"20:00";
  const [notifH,notifM]=notifTime.split(":").map(Number);
  const pastReminderTime=!todayDone&&nowHM>=(notifH*60+notifM);
  // Chi puoi mandare una pagina, per davvero: chi già ti scrive + chi segui
  // e non hai ancora una conversazione aperta, unite in una sola lista
  // senza doppioni (per username).
  const [realRecipients,setRealRecipients]=useState([]);
  useEffect(()=>{
    if(!userId) return;
    let mounted=true;
    (async()=>{
      const [existingThreads,following]=await Promise.all([fetchMyThreads(userId),fetchMyFollowing(userId)]);
      if(!mounted) return;
      const fromThreads=existingThreads.map(th=>({id:th.theirId,username:th.user,theme:null,avatarUrl:th.avatarUrl}));
      const known=new Set(fromThreads.map(r=>r.username));
      const fromFollowing=following.filter(f=>!known.has(f.username));
      setRealRecipients([...fromThreads,...fromFollowing]);
    })();
    return ()=>{ mounted=false; };
  },[userId]);
  const [showFollowedBook,setShowFollowedBook]=useState(false);
  useEffect(()=>{ dispatch({type:"SET_FEED_VIEWED",ts:Date.now()}); },[]);
  const scrollRef=useRef(null);
  const pullState=useRef({startY:0,pulling:false});
  const [pullDist,setPullDist]=useState(0);
  const [refreshing,setRefreshing]=useState(false);
  const PULL_THRESHOLD=68;
  const doRefresh=async()=>{
    setRefreshing(true);
    const items=await fetchRealFeed(userId);
    dispatch({type:"LOAD_FEED",items});
    setRefreshing(false);
    setPullDist(0);
  };
  const onScrollTouchStart=(e)=>{
    if(scrollRef.current&&scrollRef.current.scrollTop<=0){
      pullState.current={startY:e.touches[0].clientY,pulling:true};
    } else {
      pullState.current.pulling=false;
    }
  };
  const onScrollTouchMove=(e)=>{
    if(!pullState.current.pulling||refreshing) return;
    const dy=e.touches[0].clientY-pullState.current.startY;
    if(dy>0&&scrollRef.current&&scrollRef.current.scrollTop<=0){
      setPullDist(Math.min(dy*0.5,100));
    } else {
      setPullDist(0);
    }
  };
  const onScrollTouchEnd=()=>{
    if(pullState.current.pulling&&pullDist>=PULL_THRESHOLD&&!refreshing){
      doRefresh();
    } else if(!refreshing){
      setPullDist(0);
    }
    pullState.current.pulling=false;
  };
  return(
    <div ref={scrollRef} className="screen fade" style={{background:sectionGlowBg("feed",t)}} onTouchStart={onScrollTouchStart} onTouchMove={onScrollTouchMove} onTouchEnd={onScrollTouchEnd}>
      {(pullDist>0||refreshing)&&(
        <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:Math.max(pullDist,refreshing?50:0),transition:refreshing?"height .2s":"none",overflow:"hidden",flexShrink:0}}>
          <div style={{width:20,height:20,border:`2.5px solid ${t.ac}33`,borderTopColor:t.ac,borderRadius:"50%",animation:refreshing||pullDist>=PULL_THRESHOLD?"spin .7s linear infinite":"none",transform:refreshing?"none":`rotate(${pullDist*3}deg)`}}/>
        </div>
      )}
      {/* Intestazione scorporata dal 31/08/2026 (richiesta di Matte): non è
          più una barra piena bordo a bordo attaccata al top, ma una card
          fluttuante staccata con margini e angoli arrotondati su tutti i
          lati, come la barra di navigazione in basso (vedi Navigation.jsx) —
          stesso identico vetro (background/blur/bordo/ombra), così le due
          sembrano la stessa "famiglia" di elemento. Vive solo qui nel Feed,
          non viene portata nelle altre sezioni. position:sticky con un
          top>0 la fa restare comunque agganciata durante lo scroll, ma con
          lo stacco visivo dal bordo superiore mantenuto anche da ferma. */}
      <div style={{margin:"10px 14px 12px",padding:"9px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:10,zIndex:10,borderRadius:20,background:t.bg+"78",backdropFilter:"blur(26px) saturate(200%)",WebkitBackdropFilter:"blur(26px) saturate(200%)",border:`1px solid ${t.ac}40`,boxShadow:"0 16px 32px rgba(0,0,0,.32),0 3px 10px rgba(0,0,0,.24),inset 0 1.5px 0 rgba(255,255,255,.16),inset 0 -1px 0 rgba(0,0,0,.18)",flexShrink:0}}>
        <div onClick={()=>{ if(scrollRef.current) scrollRef.current.scrollTo({top:0,behavior:"smooth"}); if(!refreshing) doRefresh(); }} style={{cursor:"pointer"}}>
          <Logo ac={t.ac} tx={t.tx} sz={26}/>
        </div>
        {/* La "barretta" della traccia era quasi invisibile (13% di opacità del
            colore soft, su sfondi sia scuri che chiarissimi con contrasto
            minimo) — segnalato da Matte il 31/08/2026: sul telefono vero si
            vedeva solo il riempimento acceso (spesso pochi px se le pagine
            postate sono poche), mai la barra vuota intorno, quindi sembrava
            "un puntino" invece di una barra che si riempie. Fix: traccia con
            bordo visibile SEMPRE (anche a 0 pagine) + riempimento con
            larghezza minima quando c'è almeno una pagina, così resta sempre
            leggibile come barra, mai come punto. flexShrink:0 sui pezzi
            interni per sicurezza, in caso di schermi molto stretti. */}
        {/* Fix 01/09/2026 (segnalato da Matte): questa pillola era rimasta
            nel vecchio sfondo pieno t.card/t.bord, unica parte non vetrata
            dell'intestazione — ora stesso vetro "leggero" del resto della
            barra (translucida, blur, bordo tinto tema), niente blocco di
            colore pieno. */}
        <div onClick={()=>go("library")} style={{display:"flex",alignItems:"center",gap:7,background:t.bg+"40",backdropFilter:"blur(14px) saturate(180%)",WebkitBackdropFilter:"blur(14px) saturate(180%)",borderRadius:40,padding:"5px 10px",border:`1px solid ${t.ac}40`,cursor:"pointer",flexShrink:0}}>
          <div style={{width:50,height:5,flexShrink:0,background:t.soft+"26",border:`1px solid ${t.ac}40`,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.max(6,yearProgress)}%`,background:t.pill,borderRadius:3,transition:"width .4s"}}/></div>
          <span style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",flexShrink:0,whiteSpace:"nowrap"}}>{tr("feed_page_abbr")} {DOY}</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:9}}>
          <div onClick={()=>go("notifications")} style={{position:"relative",cursor:"pointer"}}>
            <NavIcon type="bell" active={(st.unreadNotifCount||0)>0} ac={t.ac} soft={t.soft} size={20}/>
            {(st.unreadNotifCount||0)>0&&<div style={{position:"absolute",top:-5,right:-6,width:14,height:14,borderRadius:"50%",background:t.ac,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff",boxShadow:`0 0 0 2px ${t.bg}`}}>{st.unreadNotifCount}</div>}
          </div>
          <div onClick={()=>go("notebook")} style={{position:"relative",cursor:"pointer"}}>
            <NavIcon type="chat" active={unread>0} ac={t.ac} soft={t.soft} size={20}/>
            {unread>0&&<div style={{position:"absolute",top:-5,right:-6,width:14,height:14,borderRadius:"50%",background:t.ac,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff",boxShadow:`0 0 0 2px ${t.bg}`}}>{unread}</div>}
          </div>
          <div onClick={()=>go("profile_me")} style={{cursor:"pointer"}}><Av u={st.profile.username||"tu"} tid={theme} sz={32} photo={st.profile.avatarUrl}/></div>
        </div>
      </div>
      {/* Stories — solo micro-libri e libri condivisi, come piccoli libri (non pallini), mai le foto singole già nel feed sotto */}
      <div style={{display:"flex",gap:11,padding:"8px 13px 11px",overflowX:"auto",scrollbarWidth:"none",flexShrink:0}}>
        <div onClick={()=>go("capture")} style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:6,cursor:"pointer"}}>
          <StoryBook t={t} bg={todayDone?t.soft+"33":t.pill} emoji={todayDone?"✓":"✦"}/>
          <span style={{fontSize:9,color:t.soft,fontFamily:"Inter,sans-serif"}}>{tr("feed_you")}</span>
        </div>
        <div onClick={()=>{setShowFollowedBook(true);dispatch({type:"MARK_MICRO_PAGES_SEEN",ids:(st.followedMicroPages||[]).map(p=>p.id)});}} style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:6,cursor:"pointer"}}>
          <StoryBook t={t} bg="linear-gradient(135deg,#1a1030,#3a2060)" emoji="📖" badge={unseenMicroCount>0&&<div style={{position:"absolute",top:-3,right:-3,width:14,height:14,borderRadius:"50%",background:"#FF2D6B",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff",boxShadow:`0 0 0 2px ${t.bg}`}}>{Math.min(unseenMicroCount,9)}</div>}/>
          <span style={{fontSize:9,color:t.soft,fontFamily:"Inter,sans-serif"}}>{tr("feed_following")}</span>
        </div>
        {[...st.micro.filter(m=>!m.closed),...(st.sharedBooks||[]).filter(b=>!b.closed)].map(b=>{
          const bt=TH[b.theme]||TH.neon;
          return(
            <div key={b.id} onClick={()=>go("micro")} style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:6,cursor:"pointer"}}>
              <StoryBook t={t} bg={bt.pill} emoji={b.cover}/>
              <span style={{fontSize:9,color:t.soft,fontFamily:"Inter,sans-serif",maxWidth:52,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.title}</span>
            </div>
          );
        })}
      </div>
      {/* Filters teaser — pushes discovery of the filter gallery.
          Vetrificata il 01/09/2026 (Matte: era rimasta l'unica barra piena
          e opaca del Feed): stesso linguaggio del resto — gradiente di
          marca ancora presente ma reso translucido, blur/saturate, bordo e
          riflessi, non più un blocco di colore piatto. Le anteprime dei
          filtri ora usano IMG_FILTER_SAMPLE (una vera mini-foto) invece
          dell'emoji fissa 🌅, così si vede davvero l'effetto di ciascun
          filtro invece di un disegno finto sempre uguale. */}
      <div onClick={()=>go("filters")} style={{margin:"0 13px 12px",background:"linear-gradient(135deg,#FF2D6Bcc,#7B2FFFcc)",backdropFilter:"blur(20px) saturate(190%)",WebkitBackdropFilter:"blur(20px) saturate(190%)",border:"1px solid rgba(255,255,255,.28)",borderRadius:16,padding:"11px 13px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,boxShadow:"0 12px 24px rgba(255,45,107,.28),0 3px 8px rgba(0,0,0,.2),inset 0 1.5px 0 rgba(255,255,255,.22),inset 0 -1px 0 rgba(0,0,0,.16)"}}>
        <div style={{display:"flex"}}>
          {st.filters.slice().sort((a,b)=>b.uses-a.uses).slice(0,3).map((f,i)=>(
            <div key={f.id} style={{marginLeft:i?-10:0,boxShadow:"0 0 0 2px rgba(255,255,255,.3)",borderRadius:9,zIndex:3-i}}><FilterTile filter={f} emoji={IMG_FILTER_SAMPLE} size={30}/></div>
          ))}
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif"}}>{tr("feed_find_your_style")}</div>
          <div style={{fontSize:9.5,color:"rgba(255,255,255,.85)",fontFamily:"Inter,sans-serif",marginTop:1}}>{tr("feed_discover_filters")}</div>
        </div>
      </div>
      {/* Cards */}
      <div style={{padding:"0 13px",flex:1}}>
        {pastReminderTime&&(
          <div onClick={()=>go("capture")} style={{background:`linear-gradient(135deg,${t.ac}22,${t.ac2||t.ac}14)`,border:`1px solid ${t.ac}44`,borderRadius:16,padding:"13px",marginBottom:13,display:"flex",gap:11,alignItems:"center",cursor:"pointer"}}>
            <span style={{fontSize:24}}>⏰</span>
            <div style={{flex:1}}>
              <div style={{fontSize:12.5,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif"}}>{tr("feed_no_photo_today")}</div>
              <div style={{fontSize:11,color:t.soft,fontFamily:"Inter,sans-serif",marginTop:2}}>{tr("feed_reminder_pre")} {notifTime} {tr("feed_reminder_post")}</div>
            </div>
          </div>
        )}
        {(st.activeEvents||[]).map(ev=>(
          <div key={ev.id} onClick={()=>{if(ev.is_main)go("year_close");}} style={{background:"linear-gradient(135deg,#7B2FFF22,#FF2D6B18)",border:"1px solid #7B2FFF44",borderRadius:16,padding:"13px",marginBottom:13,display:"flex",gap:11,alignItems:"center",cursor:ev.is_main?"pointer":"default"}}>
            <span style={{fontSize:26}}>{ev.icon}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif"}}>{ev.name}</div>
              <div style={{fontSize:11,color:t.soft,fontFamily:"Inter,sans-serif",marginTop:2}}>{ev.is_main?tr("feed_year_summary_cta"):tr("feed_special_moment")}</div>
            </div>
          </div>
        ))}
        {st.memory&&(
          <div style={{background:`linear-gradient(135deg,${t.ac}22,${t.ac2||t.ac}14)`,border:`1px solid ${t.ac}44`,borderRadius:16,padding:"13px",marginBottom:13,display:"flex",gap:11,alignItems:"center"}}>
            <div style={{width:56,height:56,borderRadius:11,overflow:"hidden",flexShrink:0,position:"relative",background:t.card}}>
              {isPhotoUrl(st.memory.photo)?<img src={st.memory.photo} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>{st.memory.photo}</div>}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,fontWeight:800,color:t.ac,fontFamily:"Inter,sans-serif",textTransform:"uppercase",letterSpacing:"0.5px"}}>{tr("feed_year_ago_today")}</div>
              <div style={{fontSize:12.5,color:t.tx,fontFamily:"Inter,sans-serif",marginTop:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{st.memory.caption||`${tr("feed_page_singular")} ${st.memory.day} · ${st.memory.year}`}</div>
            </div>
          </div>
        )}
        {st.feed.map((item,i)=><FeedCard key={item.id} item={item} t={t} go={go} dispatch={dispatch} threads={st.threads} realRecipients={realRecipients} filters={st.filters} userId={userId} eager={i===0}/>)}
      </div>
      {/* BNav non è più qui: dal 31/08/2026 è un'unica istanza ferma sopra
          alle 6 sezioni, montata in App.jsx — così la curva del cambio-
          sezione passa DAVVERO sotto un vetro unico, invece di far
          incontrare due barre diverse lungo la stessa linea. */}
      {showFollowedBook&&<FollowedBookViewer pages={st.followedMicroPages} onClose={()=>setShowFollowedBook(false)}/>}
    </div>
  );
}
